import { useQuery } from '@tanstack/react-query';
import { getMinionsReport, type MinionsPayload } from './report';
import { MN_EMPTY, MN_PREVIEW } from './fixture';

/*
 * Fetches the Minions report. When the call fails we fall back:
 *   - dev  → the sample figures, so the page can be reviewed against the design
 *   - prod → zeros and "—", so a deployed build never shows invented figures
 * Either way `isPreview` is true and the header shows a "No live data" pill.
 *
 * DEV ONLY: `?preview` shows the sample figures even when the report loads,
 * and does not ask for the report at all.
 */
export function useMinionsReport(reportId: string, baseUrl: string): { data: MinionsPayload; isPreview: boolean; lastRefreshedAt: string | null } {
  const forced = import.meta.env.DEV && typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('preview');
  const query = useQuery({
    queryKey: ['minions-report', baseUrl, reportId],
    queryFn: () => getMinionsReport(reportId, baseUrl),
    enabled: !forced,
    retry: false,
    // The pipeline refreshes on a schedule; poll so an open page keeps up.
    refetchInterval: 5 * 60_000,
  });

  if (forced) return { data: MN_PREVIEW, isPreview: true, lastRefreshedAt: null };
  if (query.data) return { data: query.data.data, isPreview: false, lastRefreshedAt: query.data.meta?.last_refreshed_at ?? null };
  return { data: import.meta.env.DEV ? MN_PREVIEW : MN_EMPTY, isPreview: true, lastRefreshedAt: null };
}
